import pickBy from 'lodash/pickBy';
import type { CrmIdentityExpanded } from '../../utils/analytics';
import {
  cookies, secureStorage
} from '../../utils/storage';
import type LeadStore from '../../stores/LeadStore';
import { USER_DATA_KEY } from '../../constants/auth';
import type { IUser } from '../../stores/AuthStore';

/**
 * The first argument is event name (these are hardcoded), second argument is optional depending on type of event
 */
type HubSpotTrackingEvent = [name: string, ...payload: unknown[]];

declare global {
  interface Window {
    _hsq: HubSpotTrackingEvent[];
  }
}

/**
 * An object providing a convenient higher-level API for working with HubSpot Tracking API
 * See: https://developers.hubspot.com/docs/api/events/tracking-code
 */
class HubSpotTracking {
  /**
   * This is an "initialize or get" kind of method.
   * HubSpot docs show a copypasted `var _hsq = window._hsq = window._hsq || [];` in most functions,
   * this method is supposed to make it clearer.
   */
  private get trackingEventQueue(): HubSpotTrackingEvent[] {
    return (window._hsq = window._hsq || []);
  }

  /**
   * Identifies a HubSpot contact, and updates their profile attributes
   */
  identify = async (data?: CrmIdentityExpanded): Promise<void> => {
    const email = data?.email;
    if (!email) {
      // HubSpot does not support identifying anonymous visitors, therefore we delay identification until email is known
      return;
    }
    // Note: This mapping is also implemented in Segment. It is duplicated here for when Segment is blocked/disabled.
    // Note: `pickBy` removes properties with `undefined` values, because HubSpot treats them as 'undefined' strings
    const hubSpotCustomProfileAttributes = pickBy(
      {
        auth0_id: data.auth0_id,
        account_id: data.account_id,
        firstname: data.firstName,
        lastname: data.lastName,
        full_name: data.name,
        phone: data.phoneNumber
      },
      (value: unknown): boolean => value !== undefined
    );
    this.trackingEventQueue.push([
      'identify',
      {
        // Email is the primary means of identification on HubSpot, so it is essential
        email: data.email,
        // HubSpot also allows updating custom contact attributes by passing them as additional attributes
        ...hubSpotCustomProfileAttributes
      }
    ]);
  };

  // This is a custom method to ensure HubSpot and the messenger are tied to the best available CRM record
  // https://knowledge.hubspot.com/privacy-and-consent/what-cookies-does-hubspot-set-in-a-visitor-s-browser
  analyticsSessionId(leadStore?: LeadStore): string {
    // Look to see if we are  manually syncing a crm user who is not fully onboarding
    const params = new URLSearchParams(location.search);
    const providedUid = params.get('uid') || params.get('message');
    const user = secureStorage.get(USER_DATA_KEY) as IUser;
    const validUid = providedUid || user?.id;

    // Return and thus, correctly identify, app user and crm account - reducing duplicates even when multiple
    // emails used along a unified journey
    // Priority 1 - A valid crm user_id has been passed in the url to sync the messenger to a specific crm
    // user (overriding)
    if (validUid) {
      localStorage.setItem('_crm_id', validUid);
      localStorage.setItem('ajs_anonymous_id', validUid);
      localStorage.setItem('ajs_user_id', validUid);
      cookies.set('ajs_user_id', validUid);
      return validUid;
    }
    // Priority 2 - already started onboarding in previous session
    const crmId = localStorage.getItem('_crm_id');
    if (crmId) {
      return crmId;
    }
    // Priority 3 - A valid lead is onboarding with active crm user_id
    const onboardingLeadCrmUserId: string | undefined =
      leadStore?.lead?.user_id || leadStore?.getCurrentLead()?.user_id;
    if (leadStore?.isOnboarding && onboardingLeadCrmUserId) {
      localStorage.setItem('_crm_id', onboardingLeadCrmUserId);
      localStorage.setItem('ajs_anonymous_id', onboardingLeadCrmUserId);
      localStorage.setItem('ajs_user_id', onboardingLeadCrmUserId);
      cookies.set('ajs_user_id', validUid);
      return onboardingLeadCrmUserId;
    }
    // Priority 4 - HubSpot has recognized a CRM user and set a cross domain cookie
    // set by HubSpot script - typically the same as segmentUserCookie
    const crmUserToken = this.analyticsUserToken();
    if (crmUserToken) {
      return crmUserToken;
    }
    const crmVisitorCookie = cookies.get('visitor');
    if (crmVisitorCookie) {
      return crmVisitorCookie;
    }
    // Priority 5 - HubSpot has not yet recognized a CRM user but Segment has identified a user
    // Set by analytics script when identified (defaults to ajs_anonymous_id)
    const segmentUserCookie = cookies.get('ajs_user_id');
    if (segmentUserCookie) {
      return segmentUserCookie;
    }
    // Priority 6 - Neither Segment nor HubSpot has recognized a user, use Segment's auto-generated anonymousId
    const segmentAnonCookie = cookies.get('ajs_anonymous_id'); // set by analytics script, aka anonymousId
    return segmentAnonCookie;
  }

  /**
   * Returns a HubSpot user token
   */
  analyticsUserToken() {
    // More about cookies:
    // https://knowledge.hubspot.com/privacy-and-consent/what-cookies-does-hubspot-set-in-a-visitor-s-browser

    // `hubspotutk` is the main cookie holding visitor's identity (an opaque HubSpot-generated GUID)
    const hubspotutk = cookies.get('hubspotutk');
    if (hubspotutk) {
      return hubspotutk;
    }
    /*
     * `__hstc` is the main cookie for tracking visitors, which also includes `hubspotutk` value.
     * It contains: domain, hubspotutk, initial timestamp, last timestamp, current timestamp, session number
     *
     * For example, in `181257784.8c353f051155bc162f109d785a153210.1714074631865.1715929719184.1715937895980.12`
     * `8c353f051155bc162f109d785a153210` would be the `hubspotutk` value
     */
    return cookies.get('__hstc')?.split('.')?.[1];
  }

  track = async (event: string, additionalData?: object) => {
    this.trackingEventQueue.push([
      'trackCustomBehavioralEvent',
      {
        name: event,
        properties: {
          ...additionalData
        }
      }
    ]);
  };

  page = () => {
    this.trackingEventQueue.push(['trackPageView']);
  };
}

export default new HubSpotTracking();
